'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import indexBy from 'lodash.indexby';
import Modal from 'react-modal';
import * as Sentry from '@sentry/react';
import AuthStore from '../../stores/AuthStore';
import UserStore from '../../stores/UserStore';
import TaxonomyActions from '../../actions/TaxonomyActions';
import TaxonomyStore from '../../stores/TaxonomyStore';
import allDiets from '../../tables/diets';
import allAvoidances from '../../tables/avoidances';
import { getConfig } from '../../utils/Env';
import { updateCachedDocuments, fetchDocumentsById } from '../../utils/Content';
import allMealTypes from '../../tables/meal-types'
import { getMealSearchParamsForProfile } from '../../pro/utils/Patients';
import * as AvoidanceTree from '../../utils/avoidanceTree';
import { recomputeOptionSelection } from '../../utils/avoidanceSelection';
import './AvoidanceSelect.scss';

const APPROVED_FOOD_TAGS = ['Approved Live Ingredient', 'Approved Auto Reference', 'Approved Reference'];

export default class AvoidanceSelect extends Component {
    static propTypes = {
        profile: PropTypes.object.isRequired,
        onChangeProfile: PropTypes.func.isRequired,
        rd_override: PropTypes.bool,

        allowProfileInhibited: PropTypes.bool,
    };

    static defaultProps = {
        rd_override: false,
        allowProfileInhibited: false,
    }

    static contextTypes = {
        isPro: PropTypes.bool,
        confirm: PropTypes.func,
        showUpgradeForm: PropTypes.func,
        nutritionPatterns: PropTypes.array
    };

    constructor(props, context) {
        super(props, context);
        const { profile } = props;
        const { language, preferences: { diets = [], avoidances = [], exclude_foods = [] } } = profile;

        const dietsToShow = allDiets.filter(diet => {
            if (diet.disabled) {
                return false;
            }

            if (diet.inhibit_for_profile && !props.allowProfileInhibited) {
                return false;
            }

            return true;
        });

        this.state = {
            user: UserStore.getUser(),
            diets,
            avoidances,
            dietAvoidances: null,
            hasSetNoAvoidance: !diets.length && !avoidances.length,
            exclude_foods,
            dietsToShow,
            allergensToShow: [],
            alert: null,
            showSearch: false,
            avoidOptions: [],
            avoidResults: {},
            assets: {},
            countingAvoidances: avoidances.length || exclude_foods.length ? true : false,
            loading: TaxonomyStore.isLoading(language),
            categories: TaxonomyStore.getCategories(language),
            taxonomy: TaxonomyStore.getTaxonomy(language),
            keyIndex: TaxonomyStore.getKeyIndex(language),
            abortController: null,
        };

        this.searchForAvoids = debounce(this.searchForAvoids, 750);
    }

    inhibitUpdate = false

    // Bumped whenever the result list is replaced or cleared. A lazy category load
    // that started under an older value drops its result instead of writing it into
    // the new list.
    searchGeneration = 0

    startNewSearchGeneration = () => {
        this.searchGeneration += 1;
    }

    latestDietClick = 0;

    resetInhibitUpdate = () => {
        this.inhibitUpdate = false;
    }

    formatTextWithParentheses = (text) => {
        if (!text || typeof text !== 'string') {
            return text;
        }
        
        // Check if text contains parentheses
        const parenMatch = text.match(/^(.*?)\s*(\(.+\))$/);
        if (parenMatch) {
            const [, mainText, parenText] = parenMatch;
            return (
                <>
                    {mainText}
                    <br />
                    <span className="paren-text">{parenText}</span>
                </>
            );
        }
        
        return text;
    }

    getDietDisplayName = (dietName) => {
        const dietNameMap = {
            'Ovo-Vegetarian': 'Vegetarian (no dairy)',
            'Lacto-Vegetarian': 'Vegetarian (no eggs)',
            'Vegetarian (Lacto-ovo)': 'Vegetarian (eggs & dairy OK)'
        };

        return dietNameMap[dietName] || dietName;
    }
    
    componentDidMount = () => {
        const { profile } = this.props;

        this.setMealCounts(true);

        this.syncAssets();

        TaxonomyStore.addChangeListener(this.onTaxonomyStoreChange);
        TaxonomyActions.ensureLoaded(profile.language);
        UserStore.addChangeListener(this.onUserStoreChange);
    }

    componentWillUnmount = () => {
        TaxonomyStore.removeChangeListener(this.onTaxonomyStoreChange);
        UserStore.removeChangeListener(this.onUserStoreChange);
    }

    onTaxonomyStoreChange = () => {
        const { profile } = this.props;

        this.setState({
            loading: TaxonomyStore.isLoading(profile.language),
            categories: TaxonomyStore.getCategories(profile.language),
            taxonomy: TaxonomyStore.getTaxonomy(profile.language),
            keyIndex: TaxonomyStore.getKeyIndex(profile.language),
        }, this.syncAssets);
    }

    onUserStoreChange = () => {
        this.setState({ user: UserStore.getUser() });
    }

    UNSAFE_componentWillReceiveProps = (nextProps, nextContext) => {
        if (this.inhibitUpdate) {
            return;
        }

        const { diets = [], avoidances = [], exclude_foods = [] } = nextProps.profile.preferences;

        this.setState({ diets, avoidances, dietAvoidances: null, exclude_foods }, this.syncAssets);
    }

    updateParent = () => {
        const { diets, avoidances, exclude_foods } = this.state;
        const { profile, onChangeProfile } = this.props;

        // Create a copy of the profile to avoid mutating the original
        const updatedProfile = {
            ...profile,
            preferences: {
                ...profile.preferences,
                diets,
                avoidances,
                exclude_foods
            }
        };

        this.inhibitUpdate = true;
        onChangeProfile(updatedProfile);
        this.resetInhibitUpdate();
    }

    setMealCounts = async (removeAvoidances = false) => {
        const { abortController, avoidances, exclude_foods, total } = this.state;

        // Don't run if the user doesn't have any avoidances yet
        if (!removeAvoidances && !avoidances.length && !exclude_foods.length) {
            return;
        }

        let profile = JSON.parse(JSON.stringify(this.props.profile));
        let qualifyingTotal = 0;

        if (removeAvoidances) {
            profile.preferences.avoidances = [];
            profile.preferences.exclude_foods = [];
        }

        this.setState({ countingAvoidances: true });

        try {
            for (const { mealType, prop } of allMealTypes) {
                if (mealType === 'Snack' || !profile.preferences[prop]) {
                    continue;  // Skip snacks or inactive preferences
                }

                let params = getMealSearchParamsForProfile(mealType, profile);
                params.size = 0; // we don't need results, just the total count
                params.types = ["recipe"];

                for (const key in params.filters) {
                    if (params.filters[key].gte) {
                        delete params.filters[key].gte;
                    }
                }

                // Create a new AbortController for each request
                const ac = new AbortController();
                this.setState({ abortController: ac });

                const response = await AuthStore.fetch(getConfig('recipe_api') + '/search', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json; schema=search/advanced/1' },
                    body: JSON.stringify(params),
                }, true, false, ac);

                qualifyingTotal += response.total; // Add to cumulative total
            }

            if (removeAvoidances) {
                this.setState({ total: qualifyingTotal, countingAvoidances: false });
            } else {
                this.setState({ qualifying: qualifyingTotal, countingAvoidances: false });
            }

        } catch (error) {
            this.setState({ countingAvoidances: false });
        }
    }

    syncAssets = async () => {
        const { exclude_foods, categories, avoidances, profile_key } = this.state;

        const allergensToShow = (categories || []).filter(category => {
            if ((category.tags || []).includes('Common Avoidance')) {
                return true;
            }
            return false;
        }).sort((a, b) => a.title.localeCompare(b.title));

        const docs = await fetchDocumentsById(exclude_foods);
        const assets = indexBy(docs, 'uuid');

        this.setMealCounts();

        this.setState({ assets, allergensToShow });
    }

    syncAssetsAndUpdateParent = () => {
        this.syncAssets();
        this.updateParent();
    }

    onChangeAvoids = (newAvoids, oldAvoids) => {
        const avoidsAdded = newAvoids.filter(avoid => oldAvoids.indexOf(avoid) == -1),
            avoidsRemoved = oldAvoids.filter(avoid => newAvoids.indexOf(avoid) == -1);

        let { diets, keyIndex } = this.state;

        // If we removed an avoidance, see if that avoidance is one of the diets avoidances
        if (avoidsRemoved.length) {
            // Is this diet listed in any of the category.diets_avoiding? If so, remove that diet
            // from the list of diets (e.g.: you cannot be vegetarian and eat chicken)
            avoidsRemoved.forEach(key => {
                const category = keyIndex[key];

                if (!category) {
                    return;
                }

                diets = diets.filter(diet => !(category.diets_avoiding || []).includes(diet));
            })
        }

        this.setState({ diets, avoidances: newAvoids, hasSetNoAvoidance: false }, this.updateParent);
    }


    // Use category.diets_avoiding to determine which categories to avoid for a diet
    getDietAvoidances = (dietNames) => {
        return this.state.categories
            .filter(category => (category.diets_avoiding || []).some(name => dietNames.includes(name)))
            .map(category => category.category_key);
    }

    getCurrentDietAvoidances = () => {
        return this.state.dietAvoidances || this.getDietAvoidances(this.state.diets);
    }

    toggleDiet = async (diet) => {
        const { profile = {}, rd_override } = this.props;
        const { confirm, nutritionPatterns = [] } = this.context;
        const { user } = this.state;

        const { inhibit_change_avoidances = false } = profile || {};

        if (inhibit_change_avoidances && !rd_override) {
            return confirm(
                'To make changes to your diets or avoidances, please contact your managing health care professional',
                accept => false, reject => false, { rejectText: '' }
            );
        }

        const dietClick = ++this.latestDietClick;

        let conditionPatterns = profile.conditions.map(({ uuid }) => nutritionPatterns.find((pattern) => pattern.uuid === uuid));

        if (user) {
            conditionPatterns = await Promise.all(profile.conditions.map((condition, i) => conditionPatterns[i] || AuthStore.fetch({
                url: getConfig('users_api') + '/nutrition-patterns' + `/${condition.uuid}`
            }).catch(() => null)));
        }

        if (dietClick !== this.latestDietClick) {
            return;
        }

        const previousDietAvoidances = this.getCurrentDietAvoidances();
        const keptAvoidances = this.state.avoidances.filter(key => !previousDietAvoidances.includes(key));

        const diets = [diet.name];
        const dietAvoidances = this.getDietAvoidances(diets).filter(key => !keptAvoidances.includes(key));
        let avoidances = keptAvoidances.concat(dietAvoidances);

        conditionPatterns.filter((v) => v).forEach((nutritionPattern) => avoidances = avoidances.concat(nutritionPattern.avoidances));

        // Filter out duplicates
        avoidances = avoidances.filter((avoidance, i) => avoidances.indexOf(avoidance) === i);

        this.setState({ diets, avoidances, dietAvoidances, hasSetNoAvoidance: false }, this.updateParent);
    }

    toggleAvoidance = (tag) => {
        let { avoidances, exclude_foods, avoidOptions, avoidResults } = this.state;
        const { profile = {}, rd_override } = this.props;
        const { confirm } = this.context;

        const { inhibit_change_avoidances = false } = profile || {};

        if (inhibit_change_avoidances && !rd_override) {
            return confirm(
                'To make changes to your diets or avoidances, please contact your managing health care professional',
                accept => false, reject => false, { rejectText: '' }
            );
        }

        // Make a copy of avoidances before we mutate
        const oldAvoids = avoidances.slice();

        var i = avoidances.indexOf(tag);

        if (i == -1) {
            avoidances.push(tag);
        } else {
            avoidances.splice(i, 1);
        }

        avoidOptions = recomputeOptionSelection(avoidOptions, avoidResults, avoidances, exclude_foods);

        this.setState({
            avoidOptions,
            dietAvoidances: this.getCurrentDietAvoidances().filter(key => key !== tag),
        }, this.syncAssets);


        this.onChangeAvoids(avoidances, oldAvoids);
    }

    toggleExcludeFood = (uuid) => {
        let { exclude_foods, avoidances, avoidOptions, avoidResults } = this.state;

        const { profile = {}, rd_override } = this.props;
        const { confirm } = this.context;

        const { inhibit_change_avoidances = false } = profile || {};

        if (inhibit_change_avoidances && !rd_override) {
            return confirm(
                'To make changes to your diets or avoidances, please contact your managing health care professional',
                accept => false, reject => false, { rejectText: '' }
            );
        }

        if (exclude_foods.includes(uuid)) {
            exclude_foods.splice(exclude_foods.indexOf(uuid), 1);
        } else if (exclude_foods.length < 54) {
            exclude_foods.push(uuid);
        } else {
            return confirm(
                'Avoiding too many foods will prevent our nutrition intelligence engine from being able to recommend a meal plan for you. You cannot avoid any more foods.',
                accept => false, reject => false, { rejectText: '' }
            );
        }

        avoidOptions = recomputeOptionSelection(avoidOptions, avoidResults, avoidances, exclude_foods);

        this.setState({
            avoidOptions,
        });

        this.setState({ exclude_foods }, this.syncAssetsAndUpdateParent);
    }

    showSearch = () => {
        const { diets, avoidances, dietAvoidances, exclude_foods, hasSetNoAvoidance } = this.state;

        this.setState({
            showSearch: true,
            selectionsAtOpen: { diets, avoidances: avoidances.slice(), dietAvoidances, exclude_foods: exclude_foods.slice(), hasSetNoAvoidance },
        });
    }

    hideSearch = () => {
        this.searchForAvoids.cancel();
        this.startNewSearchGeneration();
        this.setState({ showSearch: false, avoidOptions: [], avoidResults: [], avoidTerms: '', resultAvoidTerms: '' });
    }

    cancelSearch = () => {
        this.latestDietClick += 1;
        this.setState({ ...this.state.selectionsAtOpen }, this.syncAssetsAndUpdateParent);
        this.hideSearch();
    }

    // Pure tree/selection algorithms live in utils/avoidanceTree.js (EAT-3929).
    // These thin delegators keep the component's call sites and existing tests
    // stable; they will be dropped when the search modal is split out (Phase 3).
    filterTaxonomy = (taxonomy, ancestorKeysSet) => AvoidanceTree.filterTaxonomy(taxonomy, ancestorKeysSet);

    combineTaxonomyAndElements = (filteredTaxonomy, responseElements) =>
        AvoidanceTree.combineTaxonomyAndElements(filteredTaxonomy, responseElements);

    groupCombinedArray = (sortedArray) => AvoidanceTree.groupCombinedArray(sortedArray);

    sortGroupedArray = (groupedArray) => AvoidanceTree.sortGroupedArray(groupedArray);

    markHasChildren = (avoidOptions) => AvoidanceTree.markHasChildren(avoidOptions);

    searchForAvoids = async () => {
        const { profile } = this.props;
        const { categories, taxonomy, avoidTerms, avoidances, exclude_foods, keyIndex } = this.state;

        // Don't re-search if nothing serious has changed
        if (avoidTerms === this.state.resultAvoidTerms) {
            return;
        }

        this.startNewSearchGeneration();
        const generation = this.searchGeneration;
        this.setState({ working: true });

        const params = {
            language: profile.language || 'en',
            terms: avoidTerms,
            types: ['category', 'food'],
            filters: {
                product_type: 'Food',
                '!tags': ['Common Avoidance'],
                any_of_food_tag: APPROVED_FOOD_TAGS,
            },
            sort_by: 'avoidances',
            size: 35,
        };

        const response = await AuthStore.fetch(getConfig('recipe_api') + '/search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; schema=search/advanced/1' },
            body: JSON.stringify(params),
        }, true);

        // A newer search (or a cleared box / closed modal) started while this one waited;
        // its results are for an old term, so drop them.
        if (generation !== this.searchGeneration) {
            return;
        }

        updateCachedDocuments(response.elements);

        response.elements = response.elements.sort((a, b) => {
            if (a.type === 'category' && b.type === 'food') return -1;
            if (a.type === 'food' && b.type === 'category') return 1;
            return 0;
        })

        response.elements = response.elements.map((element, index) => ({
            ...element,
            originalIndex: index
        }));

        const ancestorKeysSet = new Set(
            response.elements.flatMap(element => [
                ...(element.ancestor_keys || []),
                ...(element.category_key ? [element.category_key] : []),
            ])
        );

        // When an ancestor category has a direct child in the API response,
        // include its other children in the taxonomy so siblings are visible.
        // e.g. searching "nuts" returns Macadamia nuts → Treenuts expands → Almonds, Cashews appear.
        const apiKeys = new Set(response.elements.map(el => el.category_key).filter(Boolean));
        const apiUuids = new Set(response.elements.map(el => el.uuid).filter(Boolean));

        for (const catKey of [...ancestorKeysSet]) {
            const cat = keyIndex[catKey];
            if (!cat || !cat.children || !cat.children.length) {
                continue;
            }
            if (!cat.children.some(c => apiKeys.has(c.category_key) || apiUuids.has(c.uuid))) {
                continue;
            }
            for (const child of cat.children) {
                ancestorKeysSet.add(child.category_key);
                (child.ancestor_keys || []).forEach(ak => ancestorKeysSet.add(ak));
            }
        }

        const uuidIndex = TaxonomyStore.getUuidIndex(profile.language || 'en');
        for (const key of [...ancestorKeysSet]) {
            let node = keyIndex[key];
            while (node && node.parent_uuid && uuidIndex[node.parent_uuid]) {
                node = uuidIndex[node.parent_uuid];
                if (node.category_key) {
                    ancestorKeysSet.add(node.category_key);
                }
            }
        }

        const filteredTaxonomy = this.filterTaxonomy(taxonomy, ancestorKeysSet);

        const rankByKey = AvoidanceTree.buildKeyRankIndex(response.elements, avoidTerms);
        filteredTaxonomy.forEach(category => AvoidanceTree.stampOriginalIndex(category, rankByKey));

        let combinedArray = this.combineTaxonomyAndElements(filteredTaxonomy, response.elements);
        combinedArray = this.sortGroupedArray(this.groupCombinedArray(combinedArray)).flat();

        let currentCategories = [];

        let selectedCategories = [];

        const avoidOptions = combinedArray.map(result => {

            const isCategory = result.type === "category";
            if (isCategory) {
                if(result.indents == 0) {
                    currentCategories = [result.category_key]
                } else if (result.indents > currentCategories.length - 1) {
                    currentCategories.push(result.category_key);
                } else if (result.indents <= currentCategories.length - 1) {
                    currentCategories = currentCategories.slice(0, result.indents);
                    currentCategories.push(result.category_key);
                }
            }

            let parents = currentCategories.slice();

            // category should not be a child of itself
            if (isCategory) {
                parents = parents.filter(category => category !== result.category_key);
            }

            let selected = false;

            if (isCategory) {
                selected = avoidances.includes(result.category_key) || avoidances.some(avoidance => (result.ancestor_keys || []).includes(avoidance)) || selectedCategories.some(avoidance => (result.ancestor_keys || []).includes(avoidance));
                if (!selectedCategories.includes(result.category_key) && selected) {
                    selectedCategories.push(result.category_key);
                }
            } else {
                selected = exclude_foods.includes(result.uuid) || avoidances.some(avoidance => (result.ancestor_keys || []).includes(avoidance)) || selectedCategories.some(avoidance => (result.ancestor_keys || []).includes(avoidance));

                return AvoidanceTree.buildFoodOption({
                    element: result,
                    indents: result.indents,
                    parents,
                    selected,
                    label: this.renderFoodLabel,
                });
            }

            return AvoidanceTree.buildCategoryOption({
                node: result,
                indents: result.indents,
                parents,
                selected,
                label: this.renderCategoryLabel,
            });
        });

        // EAT-4017: only category rows that actually have children in the
        // (search-filtered) result set should render an expand chevron. A row's
        // parents chain lists every ancestor category_key, so any key that shows
        // up there is a key with at least one child present.
        this.markHasChildren(avoidOptions);

        avoidOptions.forEach(option => {
            if (option.type === 'category' && !option.hasChildren) {
                option.collapsed = true;
            }
        });

        this.setState({
            working: false,
            avoidOptions,
            avoidResults: indexBy(combinedArray, 'uuid'),
            resultAvoidTerms: avoidTerms,
            noOptionsFound: avoidOptions.length == 0,
        });
    }

    onChangeAvoidTerms = (event) => {
        let avoidTerms = event.target.value;

        avoidTerms = (avoidTerms || '').trim();

        if (!avoidTerms) {
            // Cancel a search still waiting on the debounce and reset the terms, or that
            // search fires after the box is emptied and fills the list with a stale term.
            this.searchForAvoids.cancel();
            this.startNewSearchGeneration();
            this.setState({ avoidTerms: '', resultAvoidTerms: '', avoidOptions: [] });
            return;
        }

        this.setState({ avoidTerms }, this.searchForAvoids);
    }

    isDescendantRecursive = (parentKeys, categoryKey, avoidResults) =>
        AvoidanceTree.isDescendantOf(parentKeys, categoryKey, avoidResults);

    onSelectAvoidOption = (uuid, clickedOption = null) => {
        const { user, avoidOptions, avoidResults } = this.state;
        let { avoidances, exclude_foods } = this.state;
        const { profile, rd_override } = this.props;
        const { showUpgradeForm, confirm } = this.context;
        const { capabilities = {} } = user || {};

        if (!capabilities.advanced_avoidances) {
            showUpgradeForm({ feature: 'advanced_avoidances' });
            return;
        }

        if (!(avoidResults && avoidResults[uuid])) {
            return;
        }

        const { inhibit_change_avoidances = false } = profile || {};

        if (inhibit_change_avoidances && !rd_override) {
            return confirm(
                'To make changes to your diets or avoidances, please contact your managing health care professional',
                accept => false, reject => false, { rejectText: '' }
            );
        }

        if (exclude_foods.length >= 54) {
            return confirm(
                <div>
                    <p>Avoiding too many foods will prevent our nutrition intelligence engine from being able to recommend a meal plan for you.</p>
                    <p>You cannot avoid any more foods.</p>
                </div>,
                accept => false, reject => false, { rejectText: '' }
            );
        }

        let alreadySelected = false;


        const selectedAvoidance = avoidResults[uuid]
        // A food can appear under several categories, so look at the row that was clicked,
        // not the first row with this uuid.
        const selectedOption = clickedOption || avoidOptions.find(option => option.value == uuid);

        const isChildOfAlreadySelectedCategory = (selectedOption.parents || []).some(parent => avoidances.includes(parent))

        // The user cannot make exception for children of selected avoidances
        if (isChildOfAlreadySelectedCategory) {
            return;
        }

        // Select food
        if (avoidResults[uuid].type === 'food' && !exclude_foods.includes(uuid)) {
            exclude_foods.push(uuid);
            // Select category
        } else if (avoidResults[uuid].type === 'category' && !avoidances.includes(avoidResults[uuid].category_key)) {
            avoidances.push(avoidResults[uuid].category_key);
            // Unselect
        } else {
            alreadySelected = true;
            exclude_foods = exclude_foods.filter(id => id !== uuid);
            avoidances = avoidances.filter(key => key !== avoidResults[uuid].category_key);

            // Filter out the exclude_foods and avoidances whose parent is the uuid being unselected
            const childrenToRemove = avoidOptions
                .filter(option => (option.parents || []).includes(avoidResults[uuid].category_key))
                .map(option => ({
                    value: option.value,
                    category_key: avoidResults[option.value]?.category_key
                }));

            exclude_foods = exclude_foods.filter(id => !childrenToRemove.some(child => child.value === id));
            avoidances = avoidances.filter(key => !childrenToRemove.some(child => child.category_key === key));
        }

        const updatedAvoidUptions = avoidOptions.map(option => {
            const isDirectChild = (option.parents || []).includes(selectedAvoidance.category_key);
            const isDescendant = this.isDescendantRecursive((avoidResults[option.value]?.ancestor_keys || []), selectedAvoidance.category_key, avoidResults);
            const parentsNotSelected = !(option.parents || []).some(parent => avoidances.includes(parent));

            if (isDirectChild || (isDescendant && parentsNotSelected) || option.value === uuid) {
                option.selected = !alreadySelected;
            }
            return option;
        });


        this.setState({
            avoidances,
            exclude_foods,
            avoidOptions: updatedAvoidUptions,
            dietAvoidances: this.getCurrentDietAvoidances().filter(key => key !== selectedAvoidance.category_key),
            resultAvoidTerms: '',
        }, this.syncAssetsAndUpdateParent);
    }

    onClickEatsMostThings = () => {
        const { profile, rd_override } = this.props;
        const { confirm } = this.context;

        const { inhibit_change_avoidances = false } = profile || {};

        if (inhibit_change_avoidances && !rd_override) {
            return confirm(
                'To make changes to your diets or avoidances, please contact your managing health care professional',
                accept => false, reject => false, { rejectText: '' }
            );
        }

        this.latestDietClick += 1;

        this.setState({ diets: [], avoidances: [], dietAvoidances: [], exclude_foods: [], hasSetNoAvoidance: true }, this.syncAssetsAndUpdateParent);
    }

    toggleCategory = async (event, uuid) => {
        event.stopPropagation();
        const { avoidOptions, avoidResults, keyIndex = {} } = this.state;
        const category = avoidOptions.find(option => option.value === uuid);
        if (!category) {
            return;
        }
        const selectedAvoidance = avoidResults[uuid];
        const willExpand = category.collapsed;

        if (willExpand && category.type === 'category' && !category.hasChildren
            && !category.loaded && !category.loading) {
            // A non-leaf category whose subcategories were pruned from the search gets
            // them back from the taxonomy tree. Only a leaf loads its foods directly;
            // loading a non-leaf would flatten every descendant food under it.
            const subcategories = keyIndex[category.categoryKey]?.children || [];
            if (subcategories.length) {
                this.restoreSubcategories(uuid, subcategories);
                return;
            }

            this.setCategoryFlags(uuid, { loading: true, collapsed: false, loadError: false });
            const generation = this.searchGeneration;
            let elements = [];
            try {
                elements = await this.loadCategoryFoods(category);
            } catch (error) {
                console.error(`Failed to load foods for avoidance category "${category.categoryKey}"`, error);
                Sentry.captureException(error, {
                    tags: { component: 'AvoidanceSelect', action: 'load_category_foods' },
                    extra: { categoryKey: category.categoryKey },
                });
                if (generation !== this.searchGeneration) {
                    return;
                }
                // Leave the row unloaded so the chevron stays and the next expand retries.
                this.setCategoryFlags(uuid, { loading: false, loaded: false, collapsed: true, loadError: true });
                return;
            }
            if (generation !== this.searchGeneration) {
                return;
            }
            this.insertLoadedCategoryFoods(uuid, elements);
            return;
        }

        const collapsed = !category.collapsed;
        const toggledAvoidOptions = avoidOptions.map(option =>
            option.value === uuid ? { ...option, collapsed } : option
        );

        toggledAvoidOptions.forEach(option => {
            if ((option.parents || []).includes(selectedAvoidance.category_key)) {
                option.visible = !collapsed;
            }
        });

        this.setState({
            avoidOptions: toggledAvoidOptions,
        });
    }

    setCategoryFlags = (uuid, patch) => {
        this.setState(prev => ({
            avoidOptions: prev.avoidOptions.map(option =>
                option.value === uuid ? { ...option, ...patch } : option
            ),
        }));
    }

    loadCategoryFoods = async (category) => {
        const { profile } = this.props;

        const params = {
            language: profile.language || 'en',
            types: ['food'],
            filters: {
                product_type: 'Food',
                ingredient_tags: [category.categoryKey],
                '!tags': ['Common Avoidance'],
                any_of_food_tag: APPROVED_FOOD_TAGS,
            },
            sort_by: 'avoidances',
            size: 100,
        };

        const response = await AuthStore.fetch(getConfig('recipe_api') + '/search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; schema=search/advanced/1' },
            body: JSON.stringify(params),
        }, true);

        const elements = response.elements || [];

        if (typeof response.total === 'number' && response.total > elements.length) {
            console.warn(`Avoidance category "${category.categoryKey}" has ${response.total} foods; loaded only the first ${elements.length}.`);
        }

        return elements;
    }

    restoreSubcategories = (uuid, subcategories) => {
        this.setState(prev => {
            const { avoidOptions, avoidResults, avoidances } = prev;
            const idx = avoidOptions.findIndex(option => option.value === uuid);
            if (idx === -1) {
                return null;
            }
            const category = avoidOptions[idx];
            const childParents = [...(category.parents || []), category.categoryKey];
            const childPath = childParents.join('>');
            const existing = new Set(avoidOptions
                .filter(option => (option.parents || []).join('>') === childPath)
                .map(option => option.value));
            const parentSelected = avoidances.includes(category.categoryKey)
                || (category.parents || []).some(parent => avoidances.includes(parent));

            const childOptions = subcategories
                .filter(node => node.uuid && !existing.has(node.uuid))
                .map(node => ({
                    ...AvoidanceTree.buildCategoryOption({
                        node,
                        indents: category.indents + 1,
                        parents: childParents,
                        selected: parentSelected || avoidances.includes(node.category_key),
                        visible: category.visible !== false,
                        label: this.renderCategoryLabel,
                    }),
                    // Each subcategory starts collapsed; expanding it restores or loads its own children.
                    collapsed: true,
                }));

            const newAvoidOptions = [
                ...avoidOptions.slice(0, idx + 1),
                ...childOptions,
                ...avoidOptions.slice(idx + 1),
            ];
            newAvoidOptions[idx] = { ...category, loaded: true, collapsed: childOptions.length === 0 };
            this.markHasChildren(newAvoidOptions);

            const newAvoidResults = { ...avoidResults };
            subcategories.forEach(node => {
                if (node.uuid) {
                    newAvoidResults[node.uuid] = node.type ? node : { ...node, type: 'category' };
                }
            });

            return { avoidOptions: newAvoidOptions, avoidResults: newAvoidResults };
        });
    }

    renderCategoryLabel = (option) => {
        const { capabilities = {} } = this.state.user || {};

        return (
            <li className="result-item" data-indents={option.indents}>
                {option.loading ? (
                    <i data-indents={option.indents} className="icon-spinner2 fixed-scale avoidance-category-loading" />
                ) : (option.hasChildren || !option.loaded) ? (
                    <i
                        data-indents={option.indents}
                        onClick={(event) => this.toggleCategory(event, option.value)}
                        className={option.collapsed ? "icon-chevron-right" : "icon-chevron-down"}
                    />
                ) : null}
                {option.name}
                {!capabilities.advanced_avoidances ? <i className="icon-lock" /> : null}
                {option.loadError ? (
                    <span className="avoidance-category-error">
                        Couldn't load these foods. Tap the arrow to try again.
                    </span>
                ) : null}
            </li>
        );
    }

    renderFoodLabel = (option) => {
        const { capabilities = {} } = this.state.user || {};

        return (
            <li className="result-item" data-indents={option.indents}>
                {option.name}
                {!capabilities.advanced_avoidances ? <i className="icon-lock" /> : null}
            </li>
        );
    }

    insertLoadedCategoryFoods = (uuid, elements) => {
        updateCachedDocuments(elements);

        this.setState(prev => {
            const { avoidOptions, avoidResults, avoidances, exclude_foods } = prev;
            const idx = avoidOptions.findIndex(option => option.value === uuid);
            if (idx === -1) {
                return null;
            }
            const category = avoidOptions[idx];
            const childParents = [...(category.parents || []), category.categoryKey];

            // A food may already show under another category, and it belongs here too, so
            // only skip foods that are already rows directly under this category (or that
            // repeat within this load).
            const childPath = childParents.join('>');
            const seenUuids = new Set(avoidOptions
                .filter(option => (option.parents || []).join('>') === childPath)
                .map(option => option.value));

            // The foods load under this category, so we already know their parent. If the
            // category itself (or an ancestor of it) is an avoided key, every food we just
            // loaded is avoided too. A food's own ancestor_keys can miss the category key
            // even when the food carries its ingredient tag, so we cannot rely on that
            // alone; without this, only some children would highlight when the parent is
            // selected while collapsed.
            const parentSelected = avoidances.includes(category.categoryKey)
                || (category.parents || []).some(parent => avoidances.includes(parent));

            // An ancestor may have been collapsed while the fetch ran, so derive visibility
            // from the list as it is now.
            const collapsedKeys = new Set(avoidOptions
                .filter(option => option.type === 'category' && option.collapsed)
                .map(option => option.categoryKey));
            const childrenVisible = category.visible !== false
                && !(category.parents || []).some(key => collapsedKeys.has(key));

            const childOptions = elements
                .filter(element => {
                    if (!element.uuid || seenUuids.has(element.uuid)) {
                        return false;
                    }
                    seenUuids.add(element.uuid);
                    return true;
                })
                .map(element => AvoidanceTree.buildFoodOption({
                    element,
                    indents: category.indents + 1,
                    parents: childParents,
                    selected: parentSelected
                        || exclude_foods.includes(element.uuid)
                        || avoidances.some(avoidance => (element.ancestor_keys || []).includes(avoidance)),
                    visible: childrenVisible,
                    label: this.renderFoodLabel,
                }));

            const newAvoidOptions = [
                ...avoidOptions.slice(0, idx + 1),
                ...childOptions,
                ...avoidOptions.slice(idx + 1),
            ];
            const expanded = childOptions.length > 0;
            newAvoidOptions[idx] = { ...category, loading: false, loaded: true, collapsed: !expanded };
            this.markHasChildren(newAvoidOptions);

            const newAvoidResults = { ...avoidResults };
            elements.forEach(element => {
                if (element.uuid) {
                    newAvoidResults[element.uuid] = element;
                }
            });

            return { avoidOptions: newAvoidOptions, avoidResults: newAvoidResults };
        });
    }

    renderAvoidancesSearchModal = () => {
        const { showSearch, allergensToShow, avoidances, exclude_foods, keyIndex, assets, avoidResults, avoidOptions, noOptionsFound, working, total, qualifying, countingAvoidances } = this.state;

        const totalAvoidances = avoidances.length + exclude_foods.length;

        if (!showSearch) {
            return;
        }

        return (
            <Modal isOpen={true}
                onRequestClose={this.cancelSearch}
                contentLabel="Avoidances Search"
                className="el-modal el-modal2"
                overlayClassName="el-modal-overlay"
                closeTimeoutMS={250}>

                <section className="el-modal-container el-modal2-container avoidances-modal-container el-fonts">
                    <header>
                        <h2>Avoidances</h2>
                        <button className="el-modal-close-x" onClick={this.cancelSearch}>
                            <i className="icon-close-x" />
                            <span className="assistive-text">Close Modal</span>
                        </button>
                    </header>

                    <div className="el-modal-body-container el-modal2-body-container">
                        <div className="selected-avoidances">
                            {avoidances.map(key => {
                                return (
                                    <button key={key} className="el-medium-btn el-grayish-blue-outline-btn" onClick={() => this.toggleAvoidance(key)}>
                                        {keyIndex && keyIndex[key] && this.formatTextWithParentheses(keyIndex[key].title)} <i className="icon-x" />
                                    </button>
                                );
                            })}
                            {exclude_foods.map((uuid, i) => {
                                if (!assets[uuid]) {
                                    return;
                                }

                                return (
                                    <button key={i} className="el-medium-btn el-grayish-blue-outline-btn" onClick={() => this.toggleExcludeFood(uuid)}>
                                        {this.formatTextWithParentheses(assets[uuid].pretty_name || assets[uuid].name || assets[uuid].title)} <i className="icon-x" />
                                    </button>
                                );
                            })}
                            <div className="avoidances-disclaimer" data-center={countingAvoidances}>
                                {!countingAvoidances && totalAvoidances && total && qualifying && (total - qualifying) ? `${totalAvoidances > 1 ? "These" : "This"} ${totalAvoidances} ${totalAvoidances > 1 ? "avoidances" : "avoidance"} ${totalAvoidances > 1 ? "exclude" : "excludes"} ${total - qualifying}/${total} recipes. Selecting additional avoidances will result in less meal variety.`
                                    : null}
                                {countingAvoidances ? <i className="icon-spinner2" />
                                    : null}

                            </div>

                        </div>
                        <div className="with-label">
                            <div className="el-labeled-input el-input-with-icon el-search-input available-at">
                                <i class="icon-search3 left-icon" />
                                <input type="text" placeholder="Enter food avoidance" onChange={this.onChangeAvoidTerms} />
                            </div>
                        </div>

                        <div className="avoidance-search-results">
                            {!working && !noOptionsFound ?
                                avoidOptions.filter(option => option.visible).map(option => {
                                    const isUnavoidable = (avoidResults[option.value]?.tags || []).includes("Unavoidable");
                                    return (
                                        <span
                                            key={option.rowKey || `${option.value}:${(option.parents || []).join('>')}`}
                                            className="result-item-container"
                                            data-unavoidable={isUnavoidable}
                                            data-selected={option.selected}
                                            onClick={() => {
                                                if (!isUnavoidable) {
                                                    this.onSelectAvoidOption(option.value, option);
                                                }
                                            }}>
                                            {option.label(option)}

                                        </span>
                                    );
                                })
                                : !working ? <span className="no-resuls-found">No results found. Please try refining your search terms.</span> : <div className="spinner-container"> <i className="icon-spinner" /> </div>}
                        </div>


                    </div>

                    <footer>
                        <button className="el-modal-cancel-btn" onClick={this.cancelSearch}>cancel</button>
                        <button className="el-modal-ok-btn" onClick={this.hideSearch}>done</button>
                    </footer>
                </section>
            </Modal>
        );
    }

    render() {
        const {
            diets,
            avoidances,
            exclude_foods,
            hasSetNoAvoidance,
            dietsToShow,
            allergensToShow,
            loading,
            keyIndex,
            assets
        } = this.state;

        const { isPro } = this.context;

        if (loading) {
            return (
                <div className="avoidances-select-loader">
                    <i className="icon-spinner2" />
                </div>
            );
        }

        return (
            <div className="avoidances-selector">

                <section className="choose-diets">
                    <h3>Diet</h3>
                    <p>Select a diet below and we will only show recipes that match.</p>

                    <ul className="select-bubbles">
                        <li><button onClick={this.onClickEatsMostThings} data-active={hasSetNoAvoidance ? true : false}>Eats All Things</button></li>
                        {dietsToShow.map((diet, i) => {
                            return (
                                <li key={i}>
                                    <button data-active={diets.includes(diet.name)} onClick={() => this.toggleDiet(diet)}>
                                        {this.formatTextWithParentheses(this.getDietDisplayName(diet.name))}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </section>

                <section className="choose-allergens">
                    <h3>Allergies &amp; Avoidances</h3>
                    <p>{isPro ? "Select household allergies and avoidances and we won’t show recipes with them." : "Select household allergies and avoidances and we won’t show you recipes with them. You can add more later."}</p>

                    <ul className="select-bubbles">
                        {allergensToShow.map((category, i) => (
                            <li key={i}>
                                <button data-active={avoidances.includes(category.category_key)} onClick={() => this.toggleAvoidance(category.category_key)}>
                                    {this.formatTextWithParentheses(category.title)}
                                </button>
                            </li>
                        ))}
                        {avoidances.map(key => {
                            if (keyIndex[key] && (keyIndex[key].tags || []).includes('Common Avoidance')) {
                                return;
                            }
                            return (
                                <li key={key}><button data-active={true} onClick={() => this.toggleAvoidance(key)}>
                                    {keyIndex && keyIndex[key] && this.formatTextWithParentheses(keyIndex[key].title)}
                                </button></li>
                            );
                        })}
                        {exclude_foods.map((uuid, i) => {
                            if (!assets[uuid]) {
                                return;
                            }

                            return (
                                <li key={i}><button data-active={true} onClick={() => this.toggleExcludeFood(uuid)}>
                                    {this.formatTextWithParentheses(assets[uuid].pretty_name || assets[uuid].name || assets[uuid].title)}
                                </button></li>
                            );
                        })}

                    </ul>
                </section>
                <section className="choose-dislikes">
                    <div className="add-avoided" data-is-pro={isPro}>
                        <button onClick={this.showSearch} className="sub-action-btn">
                            <i className="icon-plus-thin" /> add more
                        </button>
                    </div>
                </section>
                {this.renderAvoidancesSearchModal()}
            </div>
        );

    }
}
